/**
 * OneDrive sharing details API slice.
 * Fetches people/groups/links permissions and supports stop sharing.
 */
import { createSlice, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import {
  getOneDriveSharingDetails,
  stopOneDriveSharing,
  removeOneDriveSharingLink as removeOneDriveSharingLinkApi,
  updateOneDriveSharingRole as updateOneDriveSharingRoleApi,
} from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import { logger } from "../../../utils/logger";

const userAuth = new UserAuth();

function unwrapSharingPayload(data) {
  return data?.value != null ? data.value : data;
}

export const fetchOneDriveSharingDetails = createAsyncThunk(
  "oneDriveSharingDetails/fetch",
  async (
    { teamId, channelId, tenantId, documentId, fileRoot },
    { rejectWithValue },
  ) => {
    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error(
        "fetchOneDriveSharingDetails no access token",
        tokenResponse,
        "REDUX",
      );
      return rejectWithValue({ message: "No access token" });
    }

    try {
      const { data, status } = await getOneDriveSharingDetails(accessToken, {
        teamId,
        channelId,
        tenantId,
        documentId,
        oneDriveRoot: fileRoot ?? "",
      });

      if (status === 200) {
        return {
          details: unwrapSharingPayload(data) ?? null,
          context: { teamId, channelId, tenantId, documentId, fileRoot },
        };
      }

      return rejectWithValue({
        message: `Failed to load sharing details (${status})`,
      });
    } catch (err) {
      logger.error("fetchOneDriveSharingDetails failed", err, "REDUX");
      const message =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Failed to load sharing details";
      return rejectWithValue({
        message:
          typeof message === "string" ? message : "Failed to load sharing details",
      });
    }
  },
);

/**
 * Marks a document as shared in the loaded lists (Working Documents and
 * OneDrive/channel documents) right after a successful share, so the Sharing
 * column updates without a refresh. Payload: { documentId }.
 */
export const markOneDriveDocumentShared = createAction(
  "oneDriveSharingDetails/markShared",
);

export const stopOneDriveDocumentSharing = createAsyncThunk(
  "oneDriveSharingDetails/stopSharing",
  async (_, { getState, rejectWithValue }) => {
    const state = getState()?.OneDriveSharingDetails;
    const ctx = state?.context;
    if (!ctx?.documentId) {
      return rejectWithValue({ message: "No document selected for stop sharing" });
    }

    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error(
        "stopOneDriveDocumentSharing no access token",
        tokenResponse,
        "REDUX",
      );
      return rejectWithValue({ message: "No access token" });
    }

    try {
      const { data, status } = await stopOneDriveSharing(accessToken, {
        teamId: ctx.teamId,
        channelId: ctx.channelId,
        tenantId: ctx.tenantId,
        documentId: ctx.documentId,
        oneDriveRoot: ctx.fileRoot ?? "",
      });

      if (status === 200) {
        return unwrapSharingPayload(data) ?? null;
      }

      return rejectWithValue({
        message: `Failed to stop sharing (${status})`,
      });
    } catch (err) {
      logger.error("stopOneDriveDocumentSharing failed", err, "REDUX");
      const message =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Failed to stop sharing";
      return rejectWithValue({
        message: typeof message === "string" ? message : "Failed to stop sharing",
      });
    }
  },
);

/**
 * Deletes a sharing link, or removes one person from a "Specific people" link
 * when recipientEmail / recipientId is passed. Resolves with refreshed details.
 * @param {{ permissionId: string, recipientEmail?: string, recipientId?: string }} args
 */
export const removeOneDriveSharingLink = createAsyncThunk(
  "oneDriveSharingDetails/removeLink",
  async (
    { permissionId, recipientEmail, recipientId },
    { getState, rejectWithValue },
  ) => {
    const ctx = getState()?.OneDriveSharingDetails?.context;
    if (!ctx?.documentId || !permissionId) {
      return rejectWithValue({ message: "No sharing link selected" });
    }

    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("removeOneDriveSharingLink no access token", tokenResponse, "REDUX");
      return rejectWithValue({ message: "No access token" });
    }

    try {
      const { data, status } = await removeOneDriveSharingLinkApi(accessToken, {
        teamId: ctx.teamId,
        channelId: ctx.channelId,
        tenantId: ctx.tenantId,
        documentId: ctx.documentId,
        oneDriveRoot: ctx.fileRoot ?? "",
        permissionId,
        recipientEmail,
        recipientId,
      });

      if (status === 200) {
        return unwrapSharingPayload(data) ?? null;
      }

      return rejectWithValue({ message: `Failed to update the link (${status})` });
    } catch (err) {
      logger.error("removeOneDriveSharingLink failed", err, "REDUX");
      const message =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Failed to update the link";
      return rejectWithValue({
        message: typeof message === "string" ? message : "Failed to update the link",
      });
    }
  },
);

/**
 * Changes one person's access on the open document.
 * @param {{ recipientEmail?: string, recipientId?: string, role: "read"|"write" }} args
 */
export const updateOneDriveSharingRole = createAsyncThunk(
  "oneDriveSharingDetails/updateRole",
  async ({ recipientEmail, recipientId, role }, { getState, rejectWithValue }) => {
    const ctx = getState()?.OneDriveSharingDetails?.context;
    if (!ctx?.documentId || (!recipientEmail && !recipientId)) {
      return rejectWithValue({ message: "No person selected" });
    }

    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("updateOneDriveSharingRole no access token", tokenResponse, "REDUX");
      return rejectWithValue({ message: "No access token" });
    }

    try {
      const { data, status } = await updateOneDriveSharingRoleApi(accessToken, {
        teamId: ctx.teamId,
        channelId: ctx.channelId,
        tenantId: ctx.tenantId,
        documentId: ctx.documentId,
        oneDriveRoot: ctx.fileRoot ?? "",
        recipientEmail,
        recipientId,
        role,
      });

      if (status === 200) {
        return unwrapSharingPayload(data) ?? null;
      }

      return rejectWithValue({ message: `Failed to change access (${status})` });
    } catch (err) {
      logger.error("updateOneDriveSharingRole failed", err, "REDUX");
      const message =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Failed to change access";
      return rejectWithValue({
        message: typeof message === "string" ? message : "Failed to change access",
      });
    }
  },
);

const initialState = {
  loading: false,
  stopping: false,
  // Key of the link / link recipient being removed ("<linkId>" or "<linkId>|<person>").
  removingLinkKey: "",
  // Email / id of the person whose access is being changed.
  updatingPersonKey: "",
  linkError: "",
  isError: false,
  message: "",
  details: null,
  isOpen: false,
  // Which useOneDrive() instance opened the dialog (see useOneDrive).
  ownerId: "",
  documentName: "",
  iconSrc: "",
  isFolder: false,
  context: null,
};

const oneDriveSharingDetailsSlice = createSlice({
  name: "oneDriveSharingDetails",
  initialState,
  reducers: {
    openSharingDetailsDialog: (state, action) => {
      state.isOpen = true;
      state.ownerId = action.payload?.ownerId ?? "";
      state.documentName = action.payload?.documentName ?? "";
      state.iconSrc = action.payload?.iconSrc ?? "";
      state.isFolder = action.payload?.isFolder === true;
      state.details = null;
      state.isError = false;
      state.message = "";
      state.loading = false;
      state.stopping = false;
      state.context = null;
      state.removingLinkKey = "";
      state.updatingPersonKey = "";
      state.linkError = "";
    },
    closeSharingDetailsDialog: (state) => {
      state.isOpen = false;
      state.ownerId = "";
      state.loading = false;
      state.stopping = false;
      state.isError = false;
      state.message = "";
      state.details = null;
      state.documentName = "";
      state.iconSrc = "";
      state.isFolder = false;
      state.context = null;
      state.removingLinkKey = "";
      state.updatingPersonKey = "";
      state.linkError = "";
    },
    clearSharingDetails: (state) => {
      state.details = null;
      state.isError = false;
      state.message = "";
      state.stopping = false;
      state.linkError = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOneDriveSharingDetails.pending, (state) => {
        state.loading = true;
        state.isError = false;
        state.message = "";
        state.details = null;
        state.stopping = false;
      })
      .addCase(fetchOneDriveSharingDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.isError = false;
        state.message = "";
        state.details = action.payload?.details ?? null;
        state.context = action.payload?.context ?? null;
        if (action.payload?.details?.documentName) {
          state.documentName = action.payload.details.documentName;
        }
      })
      .addCase(fetchOneDriveSharingDetails.rejected, (state, action) => {
        state.loading = false;
        state.isError = true;
        state.message = action.payload?.message ?? "Unknown error";
        state.details = null;
      })
      .addCase(stopOneDriveDocumentSharing.pending, (state) => {
        state.stopping = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(stopOneDriveDocumentSharing.fulfilled, (state, action) => {
        state.stopping = false;
        state.isError = false;
        state.message = "";
        state.details = action.payload ?? null;
        if (action.payload?.documentName) {
          state.documentName = action.payload.documentName;
        }
      })
      .addCase(stopOneDriveDocumentSharing.rejected, (state, action) => {
        state.stopping = false;
        state.isError = true;
        state.message = action.payload?.message ?? "Failed to stop sharing";
      })
      .addCase(removeOneDriveSharingLink.pending, (state, action) => {
        const { permissionId, recipientEmail, recipientId } = action.meta.arg ?? {};
        const person = recipientEmail || recipientId;
        state.removingLinkKey = person ? `${permissionId}|${person}` : permissionId;
        state.linkError = "";
      })
      .addCase(removeOneDriveSharingLink.fulfilled, (state, action) => {
        state.removingLinkKey = "";
        state.linkError = "";
        if (action.payload) state.details = action.payload;
      })
      .addCase(removeOneDriveSharingLink.rejected, (state, action) => {
        state.removingLinkKey = "";
        state.linkError = action.payload?.message ?? "Failed to update the link";
      })
      .addCase(updateOneDriveSharingRole.pending, (state, action) => {
        const { recipientEmail, recipientId } = action.meta.arg ?? {};
        state.updatingPersonKey = recipientEmail || recipientId || "";
        state.linkError = "";
      })
      .addCase(updateOneDriveSharingRole.fulfilled, (state, action) => {
        state.updatingPersonKey = "";
        state.linkError = "";
        if (action.payload) state.details = action.payload;
      })
      .addCase(updateOneDriveSharingRole.rejected, (state, action) => {
        state.updatingPersonKey = "";
        state.linkError = action.payload?.message ?? "Failed to change access";
      });
  },
});

export const {
  openSharingDetailsDialog,
  closeSharingDetailsDialog,
  clearSharingDetails,
} = oneDriveSharingDetailsSlice.actions;

export default oneDriveSharingDetailsSlice.reducer;
